import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { t } from 'i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'sonner';
import { DefaultGuard } from '@/components/guards/default-guard';
import { TailwindIndicator } from '@/components/tailwind-indicator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { pomConfig } from '@/configs/pom-config';
import { AppLayout } from '@/layouts/app-layout';
import { ErrorsPage } from '@/pages/errors/errors-page';
import { NotFoundPage } from '@/pages/errors/not-found-page';
import { useBoundStore } from '@/stores/store';

function ReactQueryDevtoolsWithTheme() {
  const theme = useBoundStore((state) => state.userPreferences.theme);
  return pomConfig.env === 'development' ? (
    <ReactQueryDevtools initialIsOpen={false} theme={theme} />
  ) : null;
}

export const Route = createRootRoute({
  component: () => (
    <DefaultGuard>
      <ErrorBoundary
        fallbackRender={() => (
          <AppLayout>
            <div className='flex h-full w-full items-center justify-center p-4'>
              <Alert className='w-96' variant='destructive'>
                <AlertCircle className='h-4 w-4' />
                <AlertTitle>
                  {t('An error occurred', { ns: 'errors' })}
                </AlertTitle>
                <AlertDescription className='flex items-center justify-between'>
                  <span>{t('Please reload the page', { ns: 'errors' })}</span>
                </AlertDescription>
                <div className='mt-3'>
                  <Button
                    className='h-8 bg-transparent text-error-default'
                    onClick={() => window.location.reload()}
                    size='sm'
                    variant='outline'
                  >
                    <RefreshCw className='mr-2 h-3 w-3' />
                    {t('Reload', { ns: 'errors' })}
                  </Button>
                </div>
              </Alert>
            </div>
          </AppLayout>
        )}
      >
        <Outlet />
      </ErrorBoundary>
      <TailwindIndicator />
      <Toaster closeButton richColors />
      <Suspense>
        <ReactQueryDevtoolsWithTheme />
      </Suspense>
    </DefaultGuard>
  ),
  notFoundComponent: () => (
    <ErrorsPage>
      <NotFoundPage />
    </ErrorsPage>
  ),
});
